// Tiny markdown + mention renderer for brief comments. Bounded scope —
// bold / italic / links / line breaks + @-mention sentinels — so we don't
// pull in a 50 kB react-markdown.
//
// Mention sentinel: @[u:<uuid>:Display Name]
//   The UUID is the structured reference (survives renames); the display
//   name is what the composer textarea shows so it reads naturally. The
//   renderer treats the whole sentinel as a single chip.
//
// Output is safe HTML for dangerouslySetInnerHTML: every value is escaped
// before being interpolated, link hrefs are scheme-checked, and the
// markdown replacements only ever insert tags we control.

const MENTION_RE = /@\[u:([0-9a-f-]{36}):([^\]]+)\]/g;

// Placeholder uses Unicode Private Use Area code points that user input
// cannot reasonably contain and that escapeHtml passes through unchanged
// (they're outside the entity set). Avoids collision with literal text the
// user might type (e.g. a bare "PH0").
const PH_OPEN = '';
const PH_CLOSE = '';
const PH_TOKEN_RE = new RegExp(`${PH_OPEN}(\\d+)${PH_CLOSE}`, 'g');

// Encode the bytes that matter inside HTML text. & must go first so we
// don't double-escape the entities we produce ourselves.
export function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Reject anything that isn't http(s), a relative path, or mailto:.
// `javascript:` URLs and friends collapse to `#` so the link still renders
// but can't execute.
function safeHref(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '#';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/')) return trimmed;
  return '#';
}

// Pull every well-formed mention sentinel's UUID out of `content`. Used at
// post / edit time to populate brief_comments.mention_user_ids — the
// dispatcher reads that column, never the raw content.
export function parseMentionUserIds(content) {
  const ids = new Set();
  const text = String(content || '');
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) ids.add(m[1]);
  return Array.from(ids);
}

// Stash markdown / mention matches as placeholder tokens so the
// HTML-escape pass that follows doesn't mangle the markup we just built.
function placeholderRender(content) {
  const placeholders = [];
  const stash = (html) => {
    const idx = placeholders.length;
    placeholders.push(html);
    return `${PH_OPEN}${idx}${PH_CLOSE}`;
  };

  let text = String(content || '');

  // 1. Mentions — escape the display name only (the UUID matches /[0-9a-f-]/
  //    so it can never carry HTML).
  text = text.replace(MENTION_RE, (_, _uuid, display) => stash(
    `<span class="bc-mention">@${escapeHtml(display)}</span>`
  ));

  // 2. Links — escape label and href independently. href is scheme-checked.
  text = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, url) => stash(
    `<a href="${escapeHtml(safeHref(url))}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`
  ));

  // 3. Bold then italic. Bold first so **x** wins over *x* on shared chars.
  //    [\s\S] so an emphasis run can wrap a soft break. escapeHtml on the
  //    inner text covers any < or & the user typed inside the run.
  text = text.replace(/\*\*([\s\S]+?)\*\*/g, (_, inner) => stash(
    `<strong>${escapeHtml(inner)}</strong>`
  ));
  text = text.replace(/\*([\s\S]+?)\*/g, (_, inner) => stash(
    `<em>${escapeHtml(inner)}</em>`
  ));

  return { text, placeholders };
}

// Compose the safe HTML string for a comment body.
//   1. Stash mention / link / bold / italic into placeholders (pre-escaped
//      HTML), leaving PUA-bracketed tokens in the text.
//   2. HTML-escape the remaining literal text. PUA tokens pass through
//      unchanged because they're outside the entity set.
//   3. Substitute the placeholders back in.
//   4. Wrap paragraphs and soft breaks.
export function renderCommentHtml(content) {
  if (!content) return '';
  const { text, placeholders } = placeholderRender(content);
  let escaped = escapeHtml(text);
  escaped = escaped.replace(PH_TOKEN_RE, (_, idx) => placeholders[Number(idx)] || '');
  const paragraphs = escaped.split(/\n{2,}/).map(p => p.replace(/\n/g, '<br>'));
  return `<p>${paragraphs.join('</p><p>')}</p>`;
}
